import React, { useState, useEffect, useCallback } from "react";
import connect from "react-redux/es/connect/connect";
import { addNotification as notify } from "reapop";
import AdminConfirmationModal from "../adminConfirmationModal.js";
import AdminNavigation from "../AdminNavigation";
import Select from "../../../forms/Select";
import { validateField } from "../../../pages/functions";
import { COUNTRIES, REF_DATA } from "../../../constants";
import { getApplicationRegex } from "../../plugins/utils";
import { api_v2 } from "../../NewIndex.api";
import EditMenu from "./EditMenu";
import OrderMenuList from "./OrderMenuList";
import "./style.css";

const MenuManagement = (props) => {
  const menuFields = {
    affiliate: {
      name: "Affiliate",
      value: "",
      rules: { title: "Affiliate", required: true },
    },
    country: {
      name: "Country",
      value: "",
      rules: { title: "Country", required: true },
    },
    userType: {
      name: "User Type",
      value: "",
      rules: { title: "User Type", required: true },
    },
    language: {
      name: "Language",
      value: "",
      rules: { title: "Language", required: true },
    },
  };

  const showNotification = (title, message, status) => {
    props.notify({
      title,
      message,
      status,
      position: "tr",
      dismissible: true,
      dismissAfter: 3000,
    });
  };

  const [fields, setFields] = useState(menuFields);
  const userTypeOptions = [
    { value: "HCP", label: "HCP" },
    { value: "ABBVIE_EMPLOYEE", label: "Employee" },
    { value: "Patient", label: "Patient" },
  ];

  const [languageOptions, setLanguageOptions] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);
  const [affiliateOptions, setAffiliateOptions] = useState([]);
  const [activeTab, setActiveTab] = useState("EditMenuList");
  const [menuData, setMenuData] = useState([]);
  const [templateListOptions, setTemplateListOptions] = useState([]);
  const [showAdminConfirmationModal, setShowAdminConfirmationModal] =
    useState(false);
  const [modalBodyContent, setModalBodyContent] = useState("");
  const [errors, setErrors] = useState({});

  const takeAction = useCallback(() => {
    const payload = {
      affiliate: fields.affiliate.value,
      country: fields.country.value,
      userType: fields.userType.value,
      language: fields.language.value,
      menuRule: menuData,
    };
    api_v2.saveMenuList(payload, (res) => {
      if (res) {
        setShowAdminConfirmationModal(false);
        setActiveTab("EditMenuList");
        setMenuData([]);
        showNotification("Success", "Menu list saved successfully", "success");
      } else {
        setShowAdminConfirmationModal(false);
        showNotification(
          "Error",
          "Something went wrong, Please try again later",
          "error"
        );
      }
    });
  }, [menuData, fields]);

  const resetState = () => {
    let fieldsValue = fields;
    fieldsValue.affiliate.value = "";
    fieldsValue.country.value = "";
    fieldsValue.userType.value = "";
    fieldsValue.language.value = "";
    setFields({ ...fieldsValue });
    setMenuData([]);
    setActiveTab("EditMenuList");
  };

  const getLanguageList = useCallback(() => {
    const languageOptions = [];
    api_v2.fetchTETLanguage((JsonKeys) => {
      JsonKeys.forEach((languageList) => {
        languageOptions.push({
          value: languageList.code,
          label: languageList.language,
        });
      });
      setLanguageOptions(languageOptions);
    });
  }, []);

  const onlyUnique = (value, index, self) => {
    return self.indexOf(value) === index;
  };

  useEffect(() => {
    const countryList = props.referenceData.filter(
      (item) => item.type === COUNTRIES
    )[0].countries;
    const affiliateList = countryList
      .map((c) => c.affiliate)
      .filter(onlyUnique)
      .sort();
    const options = affiliateList.map((affiliate) => ({
      value: affiliate,
      label: affiliate,
    }));
    setAffiliateOptions(options);
    if (languageOptions.length === 0) {
      getLanguageList();
    }
  }, [props.referenceData]);

  useEffect(() => {
    api_v2.fetchTemplateLive((res) => {
      setTemplateListOptions(
        res.map((item) => ({ value: item.templateName, label: item.templateName }))
      );
    });
  }, []);

  const validate = useCallback(
    (e, field) => {
      const inputValue = e.target.value.toLowerCase();
      const ref_Data = props.referenceData.find(function (category) {
        return category.type === REF_DATA;
      }).referenceData;
      const regex = getApplicationRegex(ref_Data);

      if (regex.Emoji_Regex !== null && regex.Emoji_Regex.test(inputValue)) {
        // Emoji detected, prevent it from being added
        showNotification(
          "Error!",
          "Special characters are not allowed",
          "error"
        );
        return;
      } else if (
        regex.Script_Regex !== null &&
        regex.Script_Regex.test(inputValue)
      ) {
        // HTML Tags and Script detected, prevent it from being added
        showNotification(
          "Error!",
          "Submission fails. Input contains prohibited words. Please remove them and try again.",
          "error"
        );
        return;
      } else if (regex.Script_Regex === null || regex.Emoji_Regex === null) {
        showNotification(
          "Error!",
          "Validation configuration is missing. Please contact support.",
          "error"
        );
        return;
      }
      const validateFields = validateField(e, field, fields);
      setFields({ ...validateFields });
    },
    [fields]
  );

  const handleAffiliateChange = (selected) => {
    let countryRefData = props.referenceData.filter(
      (item) => item.type === COUNTRIES
    )[0].countries;
    let countryList = countryRefData.filter(
      (country) => country.affiliate === selected
    );
    let options = countryList.map((item) => ({
      value: item.name,
      label: item.name,
    }));
    setCountryOptions(options);
  };

  const handleSearchData = useCallback(() => {
    const payload = {
      affiliate: fields.affiliate.value,
      country: fields.country.value,
      userType: fields.userType.value,
      language: fields.language.value,
    };
    api_v2.fetchMenuList(payload, (res) => {
      if (res) {
        setMenuData(res);
        setActiveTab("EditMenuList");
      } else {
        showNotification(
          "Error",
          "Something went wrong, Please try again later",
          "error"
        );
      }
    });
  }, [fields]);

  const handleNext = () => {
    if (Object.keys(errors).length === 0) {
      setActiveTab("OrderMenuList");
    } else {
      alert("Please fix all errors before proceeding to the order list.");
    }
  };
  const handleSave = () => {
    if (Object.keys(errors).length === 0) {
      setShowAdminConfirmationModal(true);
      setModalBodyContent("Are you sure you want to make this changes live?");
    } else {
      alert("Please fix all errors before proceeding to the order list.");
      setActiveTab("EditMenuList");
    }
  };

  return (
    <div className="v2_container" id="manage-menu-list">
      <AdminConfirmationModal
        isModalOpen={showAdminConfirmationModal}
        toggleYes={takeAction}
        toggleCancel={() => setShowAdminConfirmationModal(false)}
        bodyContent={modalBodyContent}
      />
      <div className="row">
        <AdminNavigation className="col-xl-2 col-lg-3 col-md-4 col-6" />
        <div className="col-xl-10 col-lg-9 col-md-8 col-6 p-l-15">
          <div className="row">
            <div className="col-12 col-xl-12">
              <h3 className="mb-3 header-primary">Top Navigation Management</h3>
            </div>
          </div>
          <div className="row">
            <div className="col-md-3">
              <Select
                searchable
                clearable={false}
                options={affiliateOptions}
                field={fields.affiliate}
                validate={(e) => validate(e, fields.affiliate)}
                onChange={(selected) => handleAffiliateChange(selected)}
              />
            </div>
            <div className="col-md-3">
              <Select
                searchable
                clearable={false}
                options={countryOptions}
                field={fields.country}
                validate={(e) => validate(e, fields.country)}
              />
            </div>
            <div className="col-md-3">
              <Select
                searchable
                clearable={false}
                options={userTypeOptions}
                field={fields.userType}
                validate={(e) => validate(e, fields.userType)}
              />
            </div>
            <div className="col-md-3">
              <Select
                searchable
                clearable={false}
                options={languageOptions}
                field={fields.language}
                validate={(e) => validate(e, fields.language)}
              />
            </div>
          </div>
          <div className="row">
            <div className=" col-md-2">
              <button
                className="btn btn-lg btn-info btn-rounded save_button"
                style={{ marginTop: "10px" }}
                onClick={handleSearchData}
                disabled={
                  fields.affiliate.value === "" ||
                  fields.country.value === "" ||
                  fields.userType.value === "" ||
                  fields.language.value === ""
                }
              >
                Search
              </button>
            </div>
          </div>
          <div
            className="row"
            style={{
              marginTop: "15px",
              marginLeft: "1px !important",
              marginBottom: "15px",
            }}
          >
            <div
              className={`col-4 col-xl-4 col-sm-6 menuList ${
                activeTab === "EditMenuList" ? "activeTab" : ""
              }`}
              onClick={() => setActiveTab("EditMenuList")}
            >
              Edit Menu List
            </div>

            <div
              className={`col-4 col-xl-4 col-sm-6 menuList ${
                activeTab === "OrderMenuList" ? "activeTab" : ""
              }`}
              onClick={() => setActiveTab("OrderMenuList")}
            >
              Order Menu List
            </div>
          </div>
          {activeTab === "EditMenuList" && (
            <div className="row">
              {menuData.length > 0 && (
                <EditMenu
                  menuData={menuData}
                  setMenuData={setMenuData}
                  templateListOptions={templateListOptions}
                  errors={errors}
                  setErrors={setErrors}
                />
              )}
            </div>
          )}
          {activeTab === "OrderMenuList" && (
            <div className="row" style={{ overflowX: "scroll" }}>
              {menuData.length > 0 && (
                <OrderMenuList menuData={menuData} setMenuData={setMenuData} />
              )}
            </div>
          )}
          {menuData.length > 0 && (
            <div style={{ textAlign: "end", marginTop: "20px" }}>
              {activeTab === "EditMenuList" && (
                <button
                  className="btn btn-lg btn-rounded btn-info"
                  style={{ marginRight: "16px" }}
                  onClick={() => handleNext()}
                >
                  Next
                </button>
              )}
              {activeTab === "OrderMenuList" && (
                <button
                  className="btn btn-lg btn-rounded btn-success"
                  style={{ marginRight: "16px" }}
                  onClick={() => handleSave()}
                >
                  Save
                </button>
              )}
              <button
                className="btn btn-lg btn-rounded btn-danger"
                onClick={() => resetState()}
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  referenceData: state.referenceData,
});

export default connect(mapStateToProps, { notify })(MenuManagement);



// WEBPACK FOOTER //
// ./src/mi-app/adminPanel/Menu Management/MenuManagement.js